import React, { FC, KeyboardEvent } from 'react';
import { Tag } from 'antd';
import { Link } from 'react-router-dom';
import { ICellRendererProps } from 'common/components/table/table.types';
import { IFilterConfig } from 'common/components/table/filters/column-filter/column-filter.types';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { visitorTypeMap, visitorTypeMapColorMap, visitorTypeOptions } from 'common/constans/visitor.constants';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { StrToDateTimeLocal } from 'common/helpers/date.helper';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import {
  CitationStatus,
  citationStatusColorMap,
  citationStatusMap,
  citationStatusOptions,
} from 'features/eiq-manage/enums/citation-status.enum';
import { CitationImages } from './citation-images/citation-images';
import { formatCurrency, formatSpeed } from './helpers/citation-utils';

interface CitationNumberCellProps {
  externalId?: string | number | null;
}

const CitationNumberCell: FC<CitationNumberCellProps> = ({ externalId }) => {
  if (externalId === null || externalId === undefined) {
    return null;
  }

  return (
    <Link to={String(externalId)} target="_blank" rel="noopener noreferrer" className="citation-link">
      {externalId}
    </Link>
  );
};

export const citationsInitialColumns = [
  {
    key: 'externalId',
    title: 'Citation #',
    dataKey: 'externalId',
    width: 120,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => (
      <CitationNumberCell externalId={cell.cellData as string | number | null | undefined} />
    ),
  },
  {
    key: 'occurredAt',
    title: 'Date',
    dataKey: 'occurredAt',
    width: 190,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      return cell.cellData ? <span>{StrToDateTimeLocal(cell.cellData as string)?.format('MMM D, YYYY hh:mm:ss a')}</span> : null;
    },
  },
  {
    key: 'photosUrl',
    title: 'Photos',
    dataKey: 'photosUrl',
    width: 180,
    fixed: 1,
    resizable: false,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      const photos = [...(cell.rowData.photosUrl ?? []), ...(cell.rowData.licensePlatePhotosUrl ?? [])];
      return photos.length > 0 ? <CitationImages photosUrl={photos} /> : null;
    },
  },
  {
    key: 'ownerType',
    title: 'Type',
    dataKey: 'ownerType',
    width: 180,
    fixed: 1,
    resizable: false,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      const ownerType = cell.cellData as TrafficLogVisitorType;
      return (
        <Tag className="eiq-tag" color={visitorTypeMapColorMap[ownerType]} style={{ color: '#292B2E' }}>
          {visitorTypeMap[ownerType]}
        </Tag>
      );
    },
  },
  {
    key: 'owner_residentName',
    title: 'Resident Name',
    dataKey: 'residentName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'owner_guestName',
    title: 'Guest Name',
    dataKey: 'guestName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'owner_companyName',
    title: 'Company',
    dataKey: 'companyName',
    width: 160,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'vehicle_licensePlate',
    title: 'License Plate',
    dataKey: 'licensePlate',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'vehicle_licenseState',
    title: 'License State',
    dataKey: 'licenseState',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
  },
  {
    key: 'sequenceNumber',
    title: 'Citation Count',
    dataKey: 'sequenceNumber',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => cell.cellData ?? null,
  },
  {
    key: 'status',
    title: 'Status',
    dataKey: 'status',
    width: 120,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => {
      const status = cell.rowData.status as CitationStatus;
      return (
        <Tag className="eiq-tag" color={citationStatusColorMap[status]} style={{ color: '#292B2E' }}>
          {citationStatusMap[status]}
        </Tag>
      );
    },
  },
  {
    key: 'speedLimit',
    title: 'Speed Limit',
    dataKey: 'speedLimit',
    width: 180,
    fixed: 1,
    resizable: true,
    sortable: true,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => formatSpeed(cell.cellData as number | string | null | undefined),
  },
  {
    key: 'violatingSpeed',
    title: 'Actual Speed',
    dataKey: 'violatingSpeed',
    width: 180,
    minWidth: 180,
    resizable: true,
    sortable: true,
    flexGrow: 1,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => formatSpeed(cell.cellData as number | string | null | undefined),
  },
  {
    key: 'punishment_totalPaymentAmount',
    title: 'Fine Amount',
    dataKey: 'totalPaymentAmount',
    width: 170,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => formatCurrency(cell.rowData.totalPaymentAmount),
  },
  {
    key: 'punishment_commissionAmount',
    title: 'Fees',
    dataKey: 'commissionAmount',
    width: 170,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => formatCurrency(cell.rowData.commissionAmount),
  },
  {
    key: 'punishment_communityPaymentAmount',
    title: 'HOA Receives',
    dataKey: 'communityPaymentAmount',
    width: 150,
    fixed: 1,
    resizable: true,
    sortable: false,
    cellRenderer: (cell: ICellRendererProps<ICitationRead>) => formatCurrency(cell.rowData.communityPaymentAmount),
  },
  {
    key: 'suspensionDays',
    title: 'Citation Suspension (days)',
    dataKey: 'suspensionDays',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'revokeDrivingPrivilegesDays',
    title: 'Revoke Driving Privileges (days)',
    dataKey: 'revokeDrivingPrivilegesDays',
    width: 290,
    fixed: 1,
    resizable: true,
    sortable: false,
  },
  {
    key: 'location_cameraName',
    title: 'Camera Name',
    dataKey: 'cameraName',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
    hidden: true,
  },
  {
    key: 'location_locationName',
    title: 'Location Name',
    dataKey: 'locationName',
    width: 230,
    fixed: 1,
    resizable: true,
    sortable: false,
    hidden: true,
  },
  {
    key: 'address',
    title: 'Address',
    dataKey: 'address',
    width: 300,
    fixed: 1,
    resizable: true,
    sortable: false,
    hidden: false,
  },
];

export const citationOwnerTypeOptions = [{ label: 'Unidentified', value: 0 }, ...visitorTypeOptions];

export const citationFilterConfig: IFilterConfig[] = [
  {
    name: 'status',
    filterType: FilterTypesEnum.Equals,
    label: 'Status',
    type: {
      options: citationStatusOptions,
      type: 'SELECT',
      mode: 'multiple',
    },
  },
  {
    name: 'owner_ownerType',
    filterType: FilterTypesEnum.Equals,
    label: 'Type',
    type: {
      options: citationOwnerTypeOptions,
      type: 'SELECT',
      mode: 'multiple',
    },
  },
  {
    name: 'occurredAt',
    filterType: FilterTypesEnum.Equals,
    label: 'Dates',
    type: {
      nameEnd: 'occurredAt',
      type: 'DATE_RANGE',
    },
    onChange(filterValue, tableContext) {
      if (!filterValue) {
        return;
      }

      tableContext.updateFilter(
        'occurredAt',
        [
          { operator: FilterTypesEnum.GreaterThanOrEqual, value: filterValue.start, innerFilterType: 'AND' },
          { operator: FilterTypesEnum.LessThan, value: filterValue.end },
        ],
        'AND',
      );
    },
  },
  {
    name: 'sequenceNumber',
    filterType: FilterTypesEnum.Equals,
    label: 'Citation Count',
    type: {
      nameEnd: 'sequenceNumber',
      type: 'NUMBER_RANGE',
    },
    onChange(filterValue, tableContext) {
      if (!filterValue) {
        return;
      }

      tableContext.updateFilter(
        'sequenceNumber',
        [
          { operator: FilterTypesEnum.GreaterThanOrEqual, value: filterValue.start, innerFilterType: 'AND' },
          { operator: FilterTypesEnum.LessThanOrEqual, value: filterValue.end },
        ],
        'AND',
      );
    },
  },
];
